import { EntityLink } from '@/components/ui/entity-link';
import { cn } from '@/lib/utils';

interface VehicleHeaderCompactMetaProps {
	color?: string | null;
	make?: string | null;
	model?: string | null;
	ownerName?: string | null;
	ownerId?: string | null;
	ownerTypeLabel?: string | null;
	fallbackVehicleLabel?: string | null;
	onOwnerClick: (entityType: string, entityId: string) => void;
}

interface VehicleColorChip {
	label: string;
	hex: string | null;
	textColor: string;
}

const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const COLOR_HEX_BY_NAME: Record<string, string> = {
	black: '#000000',
	white: '#FFFFFF',
	silver: '#C0C0C0',
	gray: '#808080',
	'dark gray': '#505050',
	red: '#CC0000',
	'dark red': '#8B0000',
	blue: '#0033CC',
	'dark blue': '#00008B',
	navy: '#000080',
	'light blue': '#87CEEB',
	green: '#006400',
	'dark green': '#013220',
	'light green': '#90EE90',
	yellow: '#FFD700',
	gold: '#DAA520',
	orange: '#FF6600',
	brown: '#654321',
	beige: '#C8AD7F',
	tan: '#D2B48C',
	charcoal: '#36454F'
};

const COLOR_ALIASES: Record<string, string> = {
	bk: 'black',
	blk: 'black',
	blck: 'black',
	wh: 'white',
	wht: 'white',
	gry: 'gray',
	gy: 'gray',
	rd: 'red',
	blu: 'blue',
	gn: 'green',
	grn: 'green',
	yl: 'yellow',
	org: 'orange',
	brn: 'brown',
	slv: 'silver'
};

const normalizeHex = (hex: string) => {
	if (hex.length === 4) {
		const [hash, r, g, b] = hex;
		return `${hash}${r}${r}${g}${g}${b}${b}`.toUpperCase();
	}

	return hex.toUpperCase();
};

const isLightHexColor = (hex: string) => {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

	return luminance > 0.6;
};

const toTitleCase = (value: string) =>
	value
		.split(' ')
		.map(segment =>
			segment ? `${segment.charAt(0).toUpperCase()}${segment.slice(1)}` : ''
		)
		.join(' ');

const resolveColorChip = (color?: string | null): VehicleColorChip | null => {
	const trimmedColor = color?.trim();

	if (!trimmedColor) {
		return null;
	}

	const normalizedKey = trimmedColor.toLowerCase().replace(/\s+/g, ' ');
	const canonicalColor = COLOR_ALIASES[normalizedKey] ?? normalizedKey;
	const resolvedHex = HEX_COLOR_REGEX.test(trimmedColor)
		? normalizeHex(trimmedColor)
		: (COLOR_HEX_BY_NAME[canonicalColor] ?? null);
	const colorLabel =
		COLOR_ALIASES[normalizedKey] !== undefined
			? toTitleCase(canonicalColor)
			: trimmedColor;
	const textColor =
		resolvedHex && isLightHexColor(resolvedHex) ? '#111827' : '#FFFFFF';

	return {
		label: colorLabel,
		hex: resolvedHex,
		textColor
	};
};

const resolveMakeModel = (make?: string | null, model?: string | null) => {
	const normalizedMake = make?.trim();
	if (!normalizedMake) {
		return null;
	}

	const normalizedModel = model?.trim();
	return normalizedModel ? `${normalizedMake} ${normalizedModel}` : normalizedMake;
};

export function VehicleHeaderCompactMeta({
	color,
	make,
	model,
	ownerName,
	ownerId,
	ownerTypeLabel,
	fallbackVehicleLabel,
	onOwnerClick
}: VehicleHeaderCompactMetaProps) {
	const colorChip = resolveColorChip(color);
	const makeModel = resolveMakeModel(make, model);
	const lineText =
		makeModel ||
		(!colorChip ? fallbackVehicleLabel?.trim() || null : null);

	return (
		<div className="space-y-0.5">
			<div className="flex items-center gap-2 min-w-0">
				{colorChip && (
					<span
						className={cn(
							'inline-flex items-center rounded px-2 py-0.5 text-[11px] font-semibold border',
							!colorChip.hex &&
								'bg-muted text-muted-foreground border-border'
						)}
						style={
							colorChip.hex
								? {
										backgroundColor: colorChip.hex,
										color: colorChip.textColor,
										borderColor: `${colorChip.hex}66`
									}
								: undefined
						}
					>
						{colorChip.label}
					</span>
				)}
				{lineText && <span className="truncate">{lineText}</span>}
			</div>
			{ownerName && (
				<div className="flex items-center gap-1">
					{ownerId ? (
						<EntityLink
							entityId={ownerId}
							entityType="customers"
							displayText={ownerName}
							onEntityClick={onOwnerClick}
						/>
					) : (
						<span>{ownerName}</span>
					)}
					{ownerTypeLabel && (
						<span className="text-muted-foreground">({ownerTypeLabel})</span>
					)}
				</div>
			)}
		</div>
	);
}
